import {
  AddressForm,
  FormArray,
  ISelectType,
  useFetch,
  useFormContext,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ExternalService from "../../../../../services/external.service";
import MainApiService from "../../../../../services/mainApi.service";
import { TDonorsForm } from "../donorsSchema";
import IndividualsService from "../../../../../services/individuals.service";
import { IGeneric } from "../../../../../models/generics";

const translationPath = "page.4uDonation.donors.form.";

const NEIGHBORHOOD_ROUTE =
  (process.env.REACT_APP_MAIN || "") + "/api/v1/Neighborhood/";
const GENERICS_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";

const service = new MainApiService<IGeneric>(GENERICS_ROUTE);
const neighborhoodService = new MainApiService<ISelectType>(NEIGHBORHOOD_ROUTE);
const cepService = new ExternalService();
const individualService = new IndividualsService();

export const DonorAddresses = () => {
  const { t } = useTranslation();
  const { control, formState } = useFormContext<TDonorsForm>();
  const [addressesTypeList, setAddressesTypeList] = useState<ISelectType[]>([]);
  const { sendRequest } = useFetch(service.list);

  const loadAddressTypes = useCallback(async () => {
    const { data } = await sendRequest({ identifier: "IndividualAddressType" });
    if (data) {
      setAddressesTypeList(
        data.sort((a, b) =>
          a.code! < b.code! ? -1 : a.code! > b.code! ? 1 : 0
        )
      );
    }
  }, [sendRequest]);

  const addressesFieldArray = useFieldArray({
    control,
    name: "addressess",
  });

  const { sendRequest: deleteAddress } = useFetch(
    individualService.deleteAddress
  );

  const handleDeleteAddress = (id: string, secondId: string) => {
    return deleteAddress(secondId);
  };

  const getAvailableAddressesTypeList = useMemo(
    () =>
      (index: number): ISelectType[] => {
        const selectedTypes = addressesFieldArray.fields
          .map((field, idx) => idx !== index && (field as any).addressType?.id)
          .filter(Boolean);

        return addressesTypeList
          .filter(
            (type) => type.code === "7" || !selectedTypes.includes(type.id)
          )
          .map((type) => ({
            id: type.id,
            name: type.name || "",
            code: type.code || "",
          }));
      },
    [addressesFieldArray.fields, addressesTypeList]
  );

  useEffect(() => {
    loadAddressTypes();
  }, [loadAddressTypes]);

  return (
    <Grid item xs={12}>
      <FormArray
        title={t(translationPath + "addresses")}
        name="addressess"
        errorMessage={formState.errors?.addressess?.message}
        onDelete={handleDeleteAddress}
        addButtonLabel={t(translationPath + "new_address") || "Add Address"}
        formArray={addressesFieldArray}
        appendValue={{
          city: null,
          addressType: null,
          zipCode: "",
          addressDescription: "",
          neighborhood: null,
          neighborhoodName: "",
          number: "",
          complement: "",
          otherNeighborhood: false,
          provincy: null,
        }}
        fieldsObject={(index) => (
          <AddressForm
            cepRequest={cepService.getCep}
            neighborhoodRequest={neighborhoodService.list}
            addressesTypeList={getAvailableAddressesTypeList(index)}
            addressType={`addressess.${index}.addressType`}
            zipCode={`addressess.${index}.zipCode`}
            city={`addressess.${index}.city`}
            neighborhood={`addressess.${index}.neighborhood`}
            otherNeighborhood={`addressess.${index}.otherNeighborhood`}
            provincy={`addressess.${index}.provincy`}
            addressDescription={`addressess.${index}.addressDescription`}
            neighborhoodName={`addressess.${index}.neighborhoodName`}
            number={`addressess.${index}.number`}
            complement={`addressess.${index}.complement`}
          />
        )}
      />
    </Grid>
  );
};
